// @ts-strict-ignore
import { useApolloClient } from '@apollo/client';
import { InputBlock } from '@coast/schemas';
import { useCallback, useState } from 'react';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { FileUpload } from '@shared/components/blocks/types';
import { uploadFile } from '@shared/scenes/upload/store/actions';
import * as uuid from '@shared/util/uuid';

export interface SignatureFieldHooks {
  signatureUrl: string;
  uploadSignature(data: string, filename: string): Promise<void>;
  clearSignature(): void;
  isLoading: boolean;
  error?: string;
}

function buildFileUpload(url: string, fileName: string): FileUpload {
  return {
    url,
    name: fileName,
    contentType: 'image/png',
  };
}

export function useSignatureFieldHooks(block: InputBlock): SignatureFieldHooks {
  const client = useApolloClient();
  const { values, setFieldValue } = useCardFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentUrl = values[block.name]?.url;

  const upload = async (url: string, name: string): Promise<string> => {
    const fileId = uuid.v4();
    const file = {
      id: fileId,
      url,
      name,
      contentType: 'image/png',
    };
    return uploadFile(client, file);
  };

  const useUploadSignature = useCallback(
    async (uri: string, fileName: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const url = await upload(uri, fileName);
        setFieldValue(block.name, buildFileUpload(url, fileName));
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [block?.name, setFieldValue],
  );

  const clearSignature = () => {
    setFieldValue(block.name, null);
    setError(null);
  };

  return {
    signatureUrl: currentUrl,
    uploadSignature: useUploadSignature,
    clearSignature,
    isLoading,
    error,
  };
}
